import React from 'react';
import { Clapperboard, Package, Music, Presentation, type LucideIcon } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';

/**
 * The four WORK STREAMS a creator picks when they open the Studio (the onboarding-by-stream entry, P1.5). Each tile
 * provisions that stream's preset agent CREW (server `resolveCrew({preset})`) and pre-seeds the editor with sensible
 * defaults (mode/duration) so the value prop is one tap away. Purely presentational: labels + callback, no data
 * fetching, no store/service imports (SteveJobs layering). Monochrome + Lucide + mobile-first.
 */
export interface StreamDef {
  /** Matches a server CREW_PRESETS stream key (STREAM_PRESET_KEYS). */
  preset: 'micro_drama' | 'product_ad' | 'music_video' | 'explainer';
  label: string;
  blurb: string;
  Icon: LucideIcon;
  /** Editor defaults this stream seeds. */
  mode: 'story' | 'hook';
  duration: number;
  /**
   * FR3 momentum copy — the idea-field placeholder for THIS stream. A micro-drama creator was previously
   * shown an ad example ("a cozy morning coffee ad"), which reads as the wrong product; each stream now
   * suggests the shape of answer it actually wants.
   */
  ideaPrompt: string;
}

export const STREAMS: StreamDef[] = [
  // Micro-drama seeds 60s: the proven commercial episode is 1–2 minutes (W13 format-fit research), so the
  // default must land inside that band rather than at the old 30s, which produced a sub-format episode.
  { preset: 'micro_drama', label: 'Micro-drama', blurb: 'A serialized short drama — hook to cliffhanger.', Icon: Clapperboard, mode: 'story', duration: 60, ideaPrompt: 'e.g. she inherits her rival’s company — and his secret' },
  { preset: 'product_ad', label: 'Product ad', blurb: 'A punchy vertical spot for a physical product.', Icon: Package, mode: 'hook', duration: 15, ideaPrompt: 'e.g. a reusable cup that keeps drinks hot 12 hours' },
  { preset: 'music_video', label: 'Music video', blurb: 'Stylized visuals cut to your track.', Icon: Music, mode: 'story', duration: 30, ideaPrompt: 'e.g. a neon night drive that ends at sunrise' },
  { preset: 'explainer', label: 'Explainer', blurb: 'A clear startup explainer, problem to solution.', Icon: Presentation, mode: 'story', duration: 45, ideaPrompt: 'e.g. how our app cuts invoicing from an hour to a minute' },
];

const StreamPicker: React.FC<{ onPick: (stream: StreamDef) => void; busy?: boolean }> = ({ onPick, busy }) => (
  <div className="mx-auto max-w-3xl">
    <div className={`mb-1 ${TYPE.stageTitle} text-secondary-900 dark:text-white`}>What are you making?</div>
    <p className={`mb-5 ${TYPE.body} text-secondary-500 dark:text-secondary-400`}>
      Pick a work stream and we’ll bring in the right production crew — director, writer, composer, and a US-legal advisor.
    </p>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {STREAMS.map((s) => (
        <button
          key={s.preset}
          type="button"
          disabled={busy}
          onClick={() => onPick(s)}
          className="group flex items-start gap-3 rounded-2xl border border-secondary-200 p-4 text-left transition-colors hover:border-secondary-400 hover:bg-secondary-50 disabled:opacity-50 dark:border-secondary-800 dark:hover:border-secondary-600 dark:hover:bg-secondary-800/50"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary-900 text-white dark:bg-white dark:text-secondary-900">
            <s.Icon className="h-5 w-5" />
          </span>
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-secondary-900 dark:text-white">{s.label}</span>
            <span className="mt-0.5 block text-xs text-secondary-500 dark:text-secondary-400">{s.blurb}</span>
          </span>
        </button>
      ))}
    </div>
  </div>
);

export default StreamPicker;
